import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TreatBrief } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import TreatCard from "@/pages/treat/components/TreatCard";
import TreatWriteModal from "@/pages/treat/components/TreatWriteModal";
import GameSectionEmpty from "@/components/feature/GameSectionEmpty";
import type { GamePastItem } from "@/components/feature/GamePastStrip";

// 메인 홈 «오늘은 내가 쏜다» 섹션.
// 오늘 글이 0건이어도 섹션은 항상 보이며,
// 빈 상태일 땐 왼쪽 안내+등록 버튼, 오른쪽 지난 모임 썸네일 몇 개만 간단히 표시한다.
export default function TreatSection() {
  const { data, loading, serverTs, reload } = useApi<TreatBrief[]>("treats.php");
  const now = useNow();
  const { member, requireLogin } = useAuth();
  const [writeOpen, setWriteOpen] = useState(false);

  const list = data ?? [];
  const { today, past } = useMemo(
    () => ({
      today: list.filter((t) => Number(t.is_today) === 1),
      past: list.filter((t) => Number(t.is_today) !== 1),
    }),
    [data]
  );

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const scroll = today.length >= 3;

  const openWrite = () => {
    if (!member) {
      requireLogin("/treat");
      return;
    }
    setWriteOpen(true);
  };

  const handleCreated = (no?: number) => {
    setWriteOpen(false);
    reload();
    if (no) {
      window.setTimeout(() => {
        document
          .getElementById(`treat-${no}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
  };

  const pastItems: GamePastItem[] = past.slice(0, 5).map((t) => ({
    no: t.no,
    to: `/treat/${t.no}`,
    icon: "🍚",
    title: t.subject,
    meta: t.place || t.meet_time || "",
  }));

  return (
    <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              <span aria-hidden="true">🍚</span>
              오늘은 내가 쏜다
            </h2>
            <p className="mt-2 text-foreground-600">
              예: 오늘 점심 쏠 테니 선착순으로 오세요.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openWrite}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              내가 쏜다
            </button>
            <Link
              to="/treat"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              전체 보기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : today.length === 0 ? (
          <GameSectionEmpty
            icon="ri-hand-heart-line"
            emptyTitle="오늘은 아직 쏘는 사람이 없습니다."
            emptyDesc="첫 번째가 되어보세요!"
            registerLabel="내가 쏜다"
            onRegister={openWrite}
            pastItems={pastItems}
            pastTitle="지난 모임"
            pastEmptyText="아직 지난 모임이 없습니다."
          />
        ) : scroll ? (
          <div className="-mx-4 flex gap-5 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
            {today.map((t) => (
              <div key={t.no} className="w-[320px] shrink-0">
                <TreatCard brief={t} elapsed={elapsed} onReload={reload} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${
              today.length > 1 ? "md:grid-cols-2" : ""
            }`}
          >
            {today.map((t) => (
              <TreatCard key={t.no} brief={t} elapsed={elapsed} onReload={reload} />
            ))}
          </div>
        )}
      </div>

      <TreatWriteModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        onCreated={handleCreated}
      />
    </section>
  );
}